// Shop pickup sales-tax rates — reference view for the portal admin (R6 only).
//
// Tax follows where the handover happens, so each pickup address has its own combined rate, derived from
// Stripe's Tax engine once (Checkout rejects the performance_location parameter, so the rate cannot be
// calculated per session). This page shows what every shop is actually charging and whether that figure is
// still current for the address on file.
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const authHeaders = () => ({
  Authorization: "Bearer " + (localStorage.getItem("ogestyle_token") || ""),
});

const formatPercent = (value) =>
  value === null || value === undefined ? "—" : `${Number(value).toFixed(3)}%`;

const formatWhen = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
};

const STATUS_STYLE = {
  current: { label: "Current", color: "#166534", bg: "#f0fdf4", border: "#86efac" },
  stale: { label: "Needs refresh", color: "#92400e", bg: "#fffbeb", border: "#fde68a" },
  not_derived: { label: "Not derived", color: "#6b7280", bg: "#f9fafb", border: "#e5e7eb" },
  failed: { label: "Failed", color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
};

const ShopTaxRates = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  // Reference preview: derive an address's rate WITHOUT storing it.
  const [preview, setPreview] = useState({
    street: "",
    city: "",
    state: "",
    zipCode: "",
  });
  const [previewResult, setPreviewResult] = useState(null);
  const [previewError, setPreviewError] = useState("");
  const [previewing, setPreviewing] = useState(false);

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }
    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const decoded = JSON.parse(decodeURIComponent(escape(atob(base64))));
      const roles = decoded.roles || [];
      const isPortalAdmin =
        decoded.role === "R6" || decoded.role === "portal_admin" || roles.includes("R6");
      if (!isPortalAdmin) {
        navigate("/dashboard");
        return false;
      }
      return true;
    } catch (err) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState("");

  const refreshRates = async () => {
    setRefreshing(true);
    setRefreshMessage("");
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/tax-admin/shop-rates/refresh`,
        {},
        { headers: authHeaders() },
      );
      const failed = (response.data?.results || []).filter((r) => r.status === "failed");
      const changed = response.data?.changed || [];
      if (failed.length > 0) {
        setRefreshMessage(
          `Derived ${response.data.results.length - failed.length} of ${
            response.data.results.length
          }. Failed: ${failed.map((f) => `${f.name} — ${f.error}`).join("; ")}`,
        );
      } else if (changed.length > 0) {
        setRefreshMessage(
          `Updated ${changed.length} rate(s): ${changed
            .map((c) => `${c.name} ${Number(c.previousPercentage).toFixed(3)}% → ${Number(c.percentage).toFixed(3)}%`)
            .join("; ")}`,
        );
      } else {
        setRefreshMessage("All shops already current — nothing changed.");
      }
      await load();
    } catch (err) {
      setRefreshMessage(
        err?.response?.data?.message || "Could not refresh the rates.",
      );
    } finally {
      setRefreshing(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tax-admin/shop-rates`, {
        headers: authHeaders(),
      });
      setData(response.data);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "Could not load the shop tax rates. This page is limited to the portal admin.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!checkPortalAdmin()) return;
    load();
  }, [checkPortalAdmin, load]);

  const runPreview = async (event) => {
    event.preventDefault();
    setPreviewing(true);
    setPreviewError("");
    setPreviewResult(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/tax-admin/shop-rates/preview`,
        preview,
        { headers: authHeaders() },
      );
      setPreviewResult(response.data);
    } catch (err) {
      setPreviewError(
        err?.response?.data?.message || "Could not derive a rate for that address.",
      );
    } finally {
      setPreviewing(false);
    }
  };

  const totals = data?.totals || {};
  const registeredStates = data?.registeredStates || [];
  const byState = data?.byState ? Object.values(data.byState) : [];
  const shops = data?.shops || [];

  return (
    <PortalAdminLayout>
      <div className="portal-dashboard">
        <div className="portal-header">
          <div>
            <h1>Shop Pickup Tax Rates</h1>
            <p className="portal-subtitle">
              The sales-tax rate charged when a product is collected at each shop.
              Derived from Stripe against the shop&apos;s own pickup address —
              reference only, nothing here changes what customers are charged.
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px" }}>
            <button
              type="button"
              className="portal-btn"
              onClick={refreshRates}
              disabled={refreshing || loading}
              title="Derive or re-derive every shop's pickup rate from Stripe"
            >
              {refreshing ? "Deriving…" : "Derive rates"}
            </button>
            <button type="button" className="portal-btn" onClick={load} disabled={loading}>
              {loading ? "Loading…" : "Refresh"}
            </button>
          </div>
        </div>

        {refreshMessage && (
          <div
            className="portal-alert"
            role="status"
            style={{
              background: "#f9fafb",
              border: "1px solid #e5e7eb",
              color: "#374151",
              marginBottom: "16px",
            }}
          >
            {refreshMessage}
          </div>
        )}

        {error && (
          <div className="portal-alert portal-alert-error" role="alert">
            {error}
          </div>
        )}

        {!error && (
          <>
            <div className="portal-stats-grid">
              <div className="portal-stat-card">
                <span className="portal-stat-label">Shops</span>
                <span className="portal-stat-value">{totals.shops ?? 0}</span>
              </div>
              <div className="portal-stat-card">
                <span className="portal-stat-label">Current</span>
                <span className="portal-stat-value">{totals.current ?? 0}</span>
              </div>
              <div className="portal-stat-card">
                <span className="portal-stat-label">Needs refresh</span>
                <span className="portal-stat-value">{totals.stale ?? 0}</span>
              </div>
              <div className="portal-stat-card">
                <span className="portal-stat-label">Not derived</span>
                <span className="portal-stat-value">
                  {(totals.notDerived ?? 0) + (totals.failed ?? 0)}
                </span>
              </div>
            </div>

            <div className="portal-card" style={{ marginTop: "24px" }}>
              <h2>Registered states</h2>
              <p style={{ color: "#6b7280", fontSize: "13px", marginBottom: "12px" }}>
                Stripe only calculates tax where the platform holds an active
                registration. A shop in any other state derives 0% and is shown as
                failed below, with the reason.
              </p>
              {registeredStates.length === 0 ? (
                <p style={{ color: "#b91c1c", fontSize: "14px" }}>
                  No active state registration found — product sales collect no tax
                  anywhere.
                </p>
              ) : (
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  {registeredStates.map((state) => (
                    <span
                      key={state}
                      style={{
                        padding: "4px 10px",
                        borderRadius: "999px",
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        fontSize: "13px",
                        fontWeight: 600,
                      }}
                    >
                      {state}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="portal-card" style={{ marginTop: "24px" }}>
              <h2>By state</h2>
              {byState.length === 0 ? (
                <p style={{ color: "#6b7280" }}>No shops yet.</p>
              ) : (
                <table className="portal-table">
                  <thead>
                    <tr>
                      <th>State</th>
                      <th>Registered</th>
                      <th>Shops</th>
                      <th>Rates in use</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byState.map((bucket) => (
                      <tr key={bucket.state}>
                        <td>{bucket.state}</td>
                        <td>
                          {bucket.registered ? (
                            <span style={{ color: "#166534", fontWeight: 600 }}>Yes</span>
                          ) : (
                            <span style={{ color: "#b91c1c", fontWeight: 600 }}>No</span>
                          )}
                        </td>
                        <td>{bucket.shops}</td>
                        <td>
                          {bucket.distinctRates.length === 0
                            ? "—"
                            : bucket.distinctRates.map(formatPercent).join(", ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="portal-card" style={{ marginTop: "24px" }}>
              <h2>Per shop</h2>
              <table className="portal-table">
                <thead>
                  <tr>
                    <th>Shop</th>
                    <th>Pickup address</th>
                    <th>Rate</th>
                    <th>Status</th>
                    <th>Derived</th>
                  </tr>
                </thead>
                <tbody>
                  {shops.map((shop) => {
                    const style = STATUS_STYLE[shop.status] || STATUS_STYLE.not_derived;
                    return (
                      <tr key={shop.shopId}>
                        <td>{shop.name}</td>
                        <td style={{ fontSize: "13px", color: "#4b5563" }}>
                          {[shop.city, shop.state, shop.postalCode]
                            .filter(Boolean)
                            .join(", ")}
                        </td>
                        <td style={{ fontWeight: 600 }}>{formatPercent(shop.percentage)}</td>
                        <td>
                          <span
                            title={shop.lastError || ""}
                            style={{
                              padding: "2px 8px",
                              borderRadius: "999px",
                              fontSize: "12px",
                              fontWeight: 600,
                              color: style.color,
                              background: style.bg,
                              border: `1px solid ${style.border}`,
                            }}
                          >
                            {style.label}
                          </span>
                          {shop.lastError && (
                            <div style={{ fontSize: "12px", color: "#b91c1c", marginTop: "4px" }}>
                              {shop.lastError}
                            </div>
                          )}
                        </td>
                        <td style={{ fontSize: "13px", color: "#4b5563" }}>
                          {formatWhen(shop.derivedAt)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="portal-card" style={{ marginTop: "24px" }}>
              <h2>Check an address</h2>
              <p style={{ color: "#6b7280", fontSize: "13px", marginBottom: "16px" }}>
                Derives the rate Stripe would charge at an address, without saving
                anything. Useful before a shop outside Illinois is approved: an
                unregistered state returns 0% and says so.
              </p>
              <form onSubmit={runPreview} style={{ display: "grid", gap: "12px", maxWidth: "520px" }}>
                {[
                  ["street", "Street address"],
                  ["city", "City"],
                  ["state", "State (2 letters)"],
                  ["zipCode", "ZIP"],
                ].map(([field, label]) => (
                  <label key={field} style={{ display: "block", fontSize: "13px" }}>
                    <span style={{ display: "block", marginBottom: "4px", color: "#374151" }}>
                      {label}
                    </span>
                    <input
                      type="text"
                      value={preview[field]}
                      onChange={(e) =>
                        setPreview((prev) => ({ ...prev, [field]: e.target.value }))
                      }
                      style={{
                        width: "100%",
                        padding: "8px 10px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                      }}
                    />
                  </label>
                ))}
                <button type="submit" className="portal-btn" disabled={previewing}>
                  {previewing ? "Checking…" : "Derive rate"}
                </button>
              </form>

              {previewError && (
                <div className="portal-alert portal-alert-error" style={{ marginTop: "16px" }}>
                  {previewError}
                </div>
              )}

              {previewResult && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    background: "#f9fafb",
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                  }}
                >
                  <div style={{ fontSize: "20px", fontWeight: 700 }}>
                    {formatPercent(previewResult.percentage)}
                  </div>
                  {(previewResult.breakdown || []).map((part, index) => (
                    <div
                      key={`${part.state}-${index}`}
                      style={{ fontSize: "13px", color: "#4b5563", marginTop: "4px" }}
                    >
                      {part.percentageDecimal || "0.0"}% · {part.state || part.country || "—"} ·{" "}
                      {part.reason || "—"}
                      {part.county ? ` · ${part.county}` : ""}
                      {part.city ? ` · ${part.city}` : ""}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </PortalAdminLayout>
  );
};

export default ShopTaxRates;
